import { useContext } from "react";
import { getFingerprint } from "@thumbmarkjs/thumbmarkjs";
import LoaderContext from "../../../Context/LoaderContext/Index";
import { useInterviewRoundContext } from "../../../Context/InterviewRoundContext";
import { Question, QuestionAnswers } from "../../../Interface/Index";
import aiInterviewService from "../../../Services/ai-interview.service";
import { openNotification } from "../../../Components/Notification/Index";
import usePollingFunctions from "./usePollingFunctions";

interface InterviewStartFlowReturnType {
  handleStartInterview: () => void;
  handleCameraAccessConfirmation: () => Promise<void>;
  handleCancelCameraAccessConfirmation: () => void;
  handleCameraAccessdenied: () => void;
  handleStartInterviewConfirmation: () => Promise<void>;
  handleCancelStartInterview: () => void;
  getBrowserFingerPrint: () => Promise<string>;
}

const useInterviewStartFlow = (): InterviewStartFlowReturnType => {
  const { setLoading, setLoadingMessage } = useContext(LoaderContext);

  const {
    setActiveQuestionIndex,
    currentSession,
    setShowCamRecorder,
    isFullscreen,
    elementRef,
    activeQuestionIndex,
    setShowCameraConfirmation,
    setShowStartInterviewModal,
    setCurrentSessionQuestionAnswers,
    setCurrentSessionQuestions,
  } = useInterviewRoundContext();

  // Polling functions
  const { checkForNextRoundQuestions } = usePollingFunctions();

  const toggleToFullscreenMode = async (): Promise<boolean> => {
    if (isFullscreen) {
      console.log("Already in fullscreen mode");
      return true;
    }

    if (!elementRef.current) {
      console.error("Element reference is null - cannot enter fullscreen");
      return false;
    }

    try {
      const element = elementRef.current;

      // Check if fullscreen is supported
      const isFullscreenSupported = !!(
        element.requestFullscreen || 
        (element as any).mozRequestFullScreen || 
        (element as any).webkitRequestFullscreen || 
        (element as any).msRequestFullscreen
      );

      if (!isFullscreenSupported) {
        console.warn("Fullscreen API is not supported on this browser");
        return false;
      }

      // Check if document has focus and user activation
      if (!document.hasFocus()) {
        console.warn("Document doesn't have focus - fullscreen will likely fail");
        return false;
      }

      // Try to request fullscreen with proper error handling
      let fullscreenPromise: Promise<void> | undefined;

      if (element.requestFullscreen) {
        fullscreenPromise = element.requestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        fullscreenPromise = (element as any).mozRequestFullScreen();
      } else if ((element as any).webkitRequestFullscreen) {
        fullscreenPromise = (element as any).webkitRequestFullscreen();
      } else if ((element as any).msRequestFullscreen) {
        fullscreenPromise = (element as any).msRequestFullscreen();
      }

      if (fullscreenPromise) {
        await fullscreenPromise;
        console.log("Successfully entered fullscreen mode");
        return true;
      }

      return false;

    } catch (error: any) {
      console.error("Failed to enter fullscreen mode:", error);
      
      if (error.name === 'NotAllowedError') {
        console.warn("Fullscreen was blocked by browser - continuing without fullscreen");
      } else if (error.name === 'TypeError') {
        console.warn("Fullscreen is not available - continuing without fullscreen");
      }
      
      return false;
    }
  };

  const toggleToNormalScreenMode = async (): Promise<void> => {
    if (!isFullscreen) {
      return;
    }

    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
      console.log("Successfully exited fullscreen mode");
    } catch (error) {
      console.error("Failed to exit fullscreen mode:", error);
    }
  };

  const handleStartInterview = async () => {
    if (!currentSession) {
      console.error("No session found -> handleStartInterview");
      return;
    }

    if (currentSession.isLive) {
      console.warn("Interview is already live");
      openNotification(
        "error",
        "Session already started on another device",
        ""
      );
      return;
    }

    if (activeQuestionIndex === -1) {
      setShowCameraConfirmation(true);
    } else {
      setActiveQuestionIndex(-1);
    }
  };

  const handleCameraAccessConfirmation = async () => {
    if (!currentSession) {
      console.error("No session found -> handleCameraAccessConfirmation");
      setShowCameraConfirmation(false);
      return;
    }
    
    let stream: MediaStream | null = null;
    
    try {
      // Check permissions first
      const cameraPermission = await navigator.permissions.query({
        name: "camera" as PermissionName,
      });
      const microphonePermission = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });

      if (
        cameraPermission.state === "denied" ||
        microphonePermission.state === "denied"
      ) {
        openNotification(
          "error",
          "Access blocked",
          "Camera or Microphone access is blocked. Please enable them from your browser settings and refresh the page."
        );
        setShowCameraConfirmation(false);
        return;
      }

      // Request media access
      stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      
      setShowCameraConfirmation(false);
      setShowStartInterviewModal(true);

    } catch (err) {
      console.error("Camera or Microphone access error:", err);
      setShowCameraConfirmation(false);
      openNotification(
        "error",
        "Access error",
        "Unable to access camera or microphone. Please check your browser settings and allow access."
      );
    } finally {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    }
  };

  const handleStartInterviewConfirmation = async () => {
    if (!currentSession) {
      console.error("No session found -> handleStartInterviewConfirmation");
      setShowStartInterviewModal(false);
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage("Starting interview please wait...");
      
      const { hash } = await getFingerprint(true);
      const updatedSession = await aiInterviewService.startAIInterview(
        currentSession._id,
        hash
      );

      const sessionQuestions: Question[] = [];

      if (!updatedSession.isQuestionsGenerated) {
        setLoadingMessage("Generating questions, please wait...");
        await checkForNextRoundQuestions(updatedSession._id);
        const questions = await aiInterviewService.getQuestionsBySessionId(
          updatedSession._id,
          hash
        );
        sessionQuestions.push(...questions);
      } else {
        setLoadingMessage("Loading questions, please wait...");
        const questions = await aiInterviewService.getQuestionsBySessionId(
          updatedSession._id,
          hash
        );
        sessionQuestions.push(...questions);
      }

      const formattedAnswers: QuestionAnswers[] = sessionQuestions.map(
        (question: Question) => ({
          questionId: question._id,
          answer: question.responses?.[0]?.description ?? null,
          fileUrl:
            question.type === "fileUpload"
              ? question.responses?.[0]?.fileUrl ?? null
              : null,
        })
      );

      setCurrentSessionQuestions(sessionQuestions);
      setCurrentSessionQuestionAnswers(formattedAnswers);
      setShowStartInterviewModal(false);

      // Small delay to ensure modal is closed before starting camera and fullscreen
      setTimeout(async () => {
        try {
          setShowCamRecorder(true);
          
          await new Promise(resolve => setTimeout(resolve, 100));
          
          let fullscreenSuccess = false;
          let retryCount = 0;
          const maxRetries = 3;
          
          while (!fullscreenSuccess && retryCount < maxRetries) {
            fullscreenSuccess = await toggleToFullscreenMode();
            if (!fullscreenSuccess) {
              retryCount++;
              console.log(`Fullscreen attempt ${retryCount} failed, retrying...`);
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }
          
          if (!fullscreenSuccess) {
            console.warn("Failed to enter fullscreen after all retries");
          }
          
          setActiveQuestionIndex(0);
        } catch (error) {
          console.error("Error in post-interview setup:", error);
          setActiveQuestionIndex(0);
        }
      }, 200);

    } catch (error: any) {
      console.error("Error starting interview:", error);
      setShowStartInterviewModal(false);
      openNotification(
        "error",
        "Interview start failed",
        "Failed to start the interview. Please try again."
      );
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleCancelStartInterview = () => {
    setShowStartInterviewModal(false);
    openNotification(
      "info",
      "Interview cancelled",
      "Interview has been cancelled. You can start again when ready."
    );
  };

  const handleCancelCameraAccessConfirmation = () => {
    setShowCameraConfirmation(false);
    openNotification(
      "info",
      "Camera access denied",
      "Without camera access, you will not be able to proceed with the interview. Please allow camera access to continue."
    );
  };

  const handleCameraAccessdenied = async () => {
    if (activeQuestionIndex > -1) {
      await toggleToNormalScreenMode();
      setActiveQuestionIndex(-1);
      setShowCamRecorder(false);
      setShowCameraConfirmation(false);
      setShowStartInterviewModal(false);
      openNotification(
        "info",
        "Camera access denied",
        "Without camera access, you will not be able to proceed with the interview."
      );
    }
  };

  const getBrowserFingerPrint = async () => {
    const { hash } = await getFingerprint(true);
    return hash;
  };

  return {
    handleStartInterview,
    handleCameraAccessConfirmation,
    handleCancelCameraAccessConfirmation,
    handleCameraAccessdenied,
    handleStartInterviewConfirmation,
    handleCancelStartInterview,
    getBrowserFingerPrint,
  };
};

export default useInterviewStartFlow;